/**
 * Pure share/unshare request plan for {@link ShareWorkflowDialog}.
 *
 * Current Heidi workspace is always transported via `workspace_id`, never
 * stuffed into `team_ids`. The resolved share-target id is also excluded from
 * team diffs so unchecking Current workspace cannot re-share that grant.
 */
import type { Workflow, WorkspaceShareTarget } from '../../routines.types';

export interface WorkflowShareDiff {
  shareOrg: boolean;
  unshareOrg: boolean;
  workspaceAdd: boolean;
  workspaceRemove: boolean;
  teamAdds: string[];
  teamRemoves: string[];
}

export const sharedTeamSet = (workflow: Workflow): Set<string> => {
  const ids = [...(workflow.shared_team_ids ?? [])];
  if (workflow.team_id) ids.push(workflow.team_id);
  return new Set(ids.filter(Boolean));
};

export const isSharedWithWorkspace = (
  workflow: Workflow,
  target: Pick<WorkspaceShareTarget, 'scope' | 'target_id'> | undefined
): boolean => {
  const targetId = target?.target_id;
  if (!targetId) return false;
  if (target?.scope === 'organization') {
    return (
      workflow.visibility === 'Organization' && workflow.organization_id === targetId
    );
  }
  if (target?.scope === 'team') return sharedTeamSet(workflow).has(targetId);
  return false;
};

export function computeWorkflowShareDiff(input: {
  workspaceId: string | null | undefined;
  workspaceTargetId?: string | null;
  selectedTeamIds: string[];
  shareCurrentWorkspace: boolean;
  shareOrg: boolean;
  wasOrg: boolean;
  wasWorkspaceShared: boolean;
  initialTeamIds: string[];
}): WorkflowShareDiff {
  const {
    workspaceId,
    workspaceTargetId,
    selectedTeamIds,
    shareCurrentWorkspace,
    shareOrg,
    wasOrg,
    wasWorkspaceShared,
    initialTeamIds,
  } = input;

  const desiredTeams = new Set(selectedTeamIds);
  const initialTeams = new Set(initialTeamIds);
  for (const id of [workspaceId, workspaceTargetId]) {
    if (id) {
      desiredTeams.delete(id);
      initialTeams.delete(id);
    }
  }

  return {
    shareOrg: shareOrg && !wasOrg,
    unshareOrg: !shareOrg && wasOrg,
    workspaceAdd: Boolean(shareCurrentWorkspace && workspaceId && !wasWorkspaceShared),
    workspaceRemove: Boolean(!shareCurrentWorkspace && workspaceId && wasWorkspaceShared),
    teamAdds: [...desiredTeams].filter((id) => !initialTeams.has(id)),
    teamRemoves: [...initialTeams].filter((id) => !desiredTeams.has(id)),
  };
}
